import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { contractTextDiffers, type ContractRole, type Language } from "@talentkontor/domain";
import { api } from "../../../data";
import { Dialog, useAction } from "../../../components/ui";
import { t } from "../../../lib/sprache";
import { loadVorlage, type VertragDetail } from "./daten";
import { renderDraft, type DraftInput } from "./pruefen";
import { ROLE_LABEL } from "./modell";
import { absaetze, absatzVergleich, angabenAenderung, neuereFassung, verdichten, werteFuerFassung, type FassungZeile } from "./vorlagen-fassung";

/**
 * Hinweis im Entwurf, wenn die Vorlage inzwischen eine neuere aktive Fassung hat (K-520): Vergleich vorher/nachher als Absätze
 * des Vertragstexts (Sicht Agentur, mit den eingetragenen Angaben) und „Entwurf aktualisieren“. Parteien, Unterzeichner, Laufzeit
 * und Bezüge bleiben; eigene Änderungen am Vertragstext ersetzt die neue Fassung, darauf weist der Vergleich hin.
 */
export function NeuereFassung({ orgId, c, dirty, input, required }: {
  orgId: string;
  c: VertragDetail["contract"];
  dirty: boolean;
  /** derselbe Stand, den Editor und Vorschau rendern (Kontext, Geldformat, aktuelle Werte) */
  input: DraftInput;
  required: ContractRole[];
}) {
  const [offen, setOffen] = useState(false);
  const { busy, error, run } = useAction();
  const lang: Language = c.language;
  const alt = useQuery({ queryKey: ["contract_templates", orgId, "fassung", c.template_id], enabled: !!c.template_id,
    queryFn: () => loadVorlage(api, orgId, c.template_id!).catch(() => null) });
  const liste = useQuery({ queryKey: ["contract_templates", orgId, "fassungen", alt.data?.key, alt.data?.language], enabled: !!alt.data,
    queryFn: () => api.select<FassungZeile>("contract_templates", { columns: "id, key, language, version, status, name",
      filters: [["org_id", "eq", orgId], ["key", "eq", alt.data!.key], ["language", "eq", alt.data!.language], ["status", "eq", "active"]] }) });
  const neuZeile = neuereFassung(alt.data ?? null, c.template_version, liste.data ?? []);
  const neu = useQuery({ queryKey: ["contract_templates", orgId, "fassung", neuZeile?.id], enabled: offen && !!neuZeile,
    queryFn: () => loadVorlage(api, orgId, neuZeile!.id) });
  if (!neuZeile) return null;

  const tpl = neu.data ?? null;
  const werte = tpl ? werteFuerFassung(tpl.variables, input.defs, input.values, input.context) : null;
  const nachher = tpl && werte ? renderDraft({ ...input, body: tpl.body_html, annex: tpl.annex_html, defs: tpl.variables, values: werte }) : null;
  const vorher = renderDraft(input);
  const zeilen = nachher ? verdichten(absatzVergleich(absaetze(`${vorher.html}${vorher.annexHtml ?? ""}`), absaetze(`${nachher.html}${nachher.annexHtml ?? ""}`))) : [];
  const geaendert = zeilen.some((z) => z.art === "plus" || z.art === "minus");
  const angaben = tpl ? angabenAenderung(tpl.variables, input.defs, lang) : { neu: [], weg: [] };
  // Eigene Änderungen am Text gegenüber der bisherigen Fassung gehen beim Heben verloren
  const eigeneAenderung = !!alt.data && contractTextDiffers({ body_html: input.body, annex_html: input.annex }, alt.data);
  const andereParteien = !!tpl && (tpl.required_signers.length !== required.length || tpl.required_signers.some((r) => !required.includes(r)));

  const heben = () => run("fassung", async () => {
    if (!tpl || !werte || !nachher) return;
    await api.update("contracts", [["id", "eq", c.id], ["status", "eq", "draft"]], {
      template_id: tpl.id, template_version: tpl.version, body_html: tpl.body_html, annex_html: tpl.annex_html, variable_defs: tpl.variables,
      variables: werte, rendered_html: nachher.html, rendered_annex_html: nachher.annexHtml, annex_hidden_from: tpl.annex_hidden_from,
    }, { columns: "id" });
    setOffen(false);
  }, t(`Entwurf auf Version ${neuZeile.version} der Vorlage aktualisiert.`, `Draft updated to version ${neuZeile.version} of the template.`));

  return (
    <div className="notice stack tk-fassung-hinweis" role="note">
      <p>{t(`Es gibt eine neuere Fassung der Vorlage „${neuZeile.name}“: Version ${neuZeile.version}, dieser Entwurf nutzt Version ${c.template_version}.`,
        `There is a newer version of the template “${neuZeile.name}”: version ${neuZeile.version}; this draft uses version ${c.template_version}.`)}</p>
      <div className="row">
        <button type="button" disabled={dirty} onClick={() => setOffen(true)}>{t("Vergleichen und aktualisieren", "Compare and update")}</button>
        {dirty && <small>{t("Erst speichern.", "Save first.")}</small>}
      </div>
      <Dialog open={offen} onClose={() => setOffen(false)} title={t(`Vorlage Version ${c.template_version} → ${neuZeile.version}`, `Template version ${c.template_version} → ${neuZeile.version}`)} wide>
        {!tpl ? <p className="muted">{neu.isError ? t("Die neue Fassung konnte nicht geladen werden.", "The new version could not be loaded.") : t("Wird geladen …", "Loading …")}</p> : (
          <div className="stack">
            <p>{t("So ändert sich der Vertragstext mit deinen Angaben (Sicht Agentur). Parteien, Unterzeichner, Laufzeit und Kooperation bleiben, eingetragene Werte auch, soweit es die Angabe weiter gibt.",
              "This is how the contract text changes with your details (agency view). Parties, signers, term and deal stay, entered values too, as far as the detail still exists.")}</p>
            {eigeneAenderung && <p className="tk-error" role="alert">{t("Der Text dieses Entwurfs wurde gegenüber der bisherigen Fassung geändert. Diese Änderungen ersetzt die neue Fassung.",
              "The text of this draft was changed compared to the previous version. The new version replaces these changes.")}</p>}
            {andereParteien && <p className="notice">{t(`Die neue Fassung sieht andere Unterzeichner vor (${tpl.required_signers.map((r) => ROLE_LABEL[r]).join(", ")}). Im Entwurf bleiben sie, wie sie sind; bitte danach unter Parteien prüfen.`,
              `The new version expects other signers (${tpl.required_signers.map((r) => ROLE_LABEL[r]).join(", ")}). They stay as they are in the draft; please check under parties afterwards.`)}</p>}
            {(angaben.neu.length > 0 || angaben.weg.length > 0) && (
              <p>{angaben.neu.length > 0 && <>{t("Neue Angaben: ", "New details: ")}{angaben.neu.join(", ")}. </>}
                {angaben.weg.length > 0 && <>{t("Entfallen: ", "Dropped: ")}{angaben.weg.join(", ")}.</>}</p>
            )}
            {!geaendert ? <p className="muted">{t("Der Vertragstext bleibt mit deinen Angaben gleich.", "The contract text stays the same with your details.")}</p> : (
              <ol className="tk-fassung-vergleich" aria-label={t("Vergleich vorher und nachher", "Comparison before and after")}>
                {zeilen.map((z, i) => z.art === "ausgelassen"
                  ? <li key={i} className="tk-fassung-aus">{t(`… ${z.anzahl} Absätze unverändert`, `… ${z.anzahl} paragraphs unchanged`)}</li>
                  : <li key={i} className={`tk-fassung-${z.art}`}>
                    {z.art === "plus" ? <><span className="sr-only">{t("Neu: ", "New: ")}</span><ins>{z.text}</ins></>
                      : z.art === "minus" ? <><span className="sr-only">{t("Entfällt: ", "Removed: ")}</span><del>{z.text}</del></> : z.text}
                  </li>)}
              </ol>
            )}
            {error && <p className="tk-error" role="alert">{error}</p>}
            <div className="row">
              <button type="button" onClick={() => setOffen(false)}>{t("Abbrechen", "Cancel")}</button>
              <button type="button" className="primary" disabled={!!busy} onClick={() => void heben()}>{busy === "fassung" ? t("Wird aktualisiert …", "Updating …") : t("Entwurf aktualisieren", "Update draft")}</button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
